/**
 * Maps note write-backs onto Candid RPA chat bubbles.
 *
 * @module mapNoteWritebacksToChatItems
 */
import { CandidNoteChatItem } from "../../../pages/worklists/singleOrder/candidUpload/types/candidNoteChatItem";
import { CandidWritebackRecord } from "../../../pages/worklists/singleOrder/candidUpload/types/candidWriteback";
import { getNotePayload } from "../../../pages/worklists/singleOrder/candidUpload/utils/getNotePayload";
import { formatDate } from "../../../utils/formatDate";

/** Chat timestamp from `created_at`. */
const CHAT_TIMESTAMP_FORMAT = "ddd, M/D/YY, h:mm A";

/** Shown when `actor_email` is missing or has no local part. */
const UNKNOWN_SENDER_NAME = "Unknown";

/**
 * Turns `smith.bethann@example.com` into `Smith Bethann`.
 *
 * Splits the local part on `.`, `_`, and `-`, then title-cases each
 * segment. Empty or null emails fall back to {@link UNKNOWN_SENDER_NAME}.
 *
 * @param actorEmail - Write-back `actor_email`, or null
 * @returns Display name for the chat sender
 */
export const senderNameFromActorEmail = (actorEmail: string | null): string => {
  const localPart = (actorEmail ?? "").split("@")[0]?.trim() ?? "";
  if (!localPart) {
    return UNKNOWN_SENDER_NAME;
  }
  const words = localPart.split(/[._-]+/).filter(Boolean);
  if (words.length === 0) {
    return UNKNOWN_SENDER_NAME;
  }
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Maps one note write-back onto a chat bubble.
 *
 * @param record - Note write-back row
 * @returns Chat item, or null when the payload has no `note`
 */
const mapNoteWritebackToChatItem = (
  record: CandidWritebackRecord,
): CandidNoteChatItem | null => {
  const payload = getNotePayload(record);
  if (!payload) {
    return null;
  }
  return {
    id: record.id,
    timestamp: formatDate(record.created_at, CHAT_TIMESTAMP_FORMAT),
    senderName: senderNameFromActorEmail(record.actor_email),
    message: payload.note,
  };
};

/**
 * Converts note write-backs into chat bubbles, skipping rows with no body.
 *
 * @param records - Note group from `groupedByUseCase.note`
 * @returns Chat items in the same order as `records`
 */
export const mapNoteWritebacksToChatItems = (
  records: CandidWritebackRecord[],
): CandidNoteChatItem[] =>
  records
    .map(mapNoteWritebackToChatItem)
    .filter((item): item is CandidNoteChatItem => item !== null);
